import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Inbox } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { ContentCard, EmptyState, DataTable } from "../../../components/ui";
import { Button } from "../../../components/ui/button";
import { LeaveActionToast } from "../../leave-management/components/LeaveActionToast";
import { TablePagination } from "../../leave-management/components/TablePagination";
import { getEmpObj } from "../../../utils/sessionDB";
import { cacheTicketRemarks, createTicket, pickTicketRemarks, searchTickets } from "../../../services/ticketTrackerService";
import { TicketPriorityBadge, TicketStatusBadge } from "../components";
import {
  EMPTY_TICKET_FORM,
  TicketFormFields,
  TicketProgressField,
  formatTicketProgress,
  validateTicketForm,
} from "../components/TicketFormFields";

const PAGE_SIZE = 10;

function displayValue(value) {
  if (value == null || value === "") return "—";
  return String(value);
}

function formatDate(iso) {
  if (!iso) return "—";
  const date = new Date(String(iso).replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return String(iso);
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function TicketsTableSkeleton() {
  return (
    <div className="px-6 py-8">
      <div className="flex flex-col gap-3">
        {[1, 2, 3, 4, 5].map((n) => (
          <div key={n} className="h-11 rounded-lg bg-[var(--color-workspace)]/50 animate-pulse" />
        ))}
      </div>
    </div>
  );
}

export function TicketsListPage() {
  const navigate = useNavigate();

  const [tickets, setTickets] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [toast, setToast] = useState(null);
  const [page, setPage] = useState(1);

  const [form, setForm] = useState(() => ({
    ...EMPTY_TICKET_FORM,
    createdBy: String(getEmpObj().first_name || ""),
  }));
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [formBanner, setFormBanner] = useState(null);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);


  const loadTickets = useCallback(async (pageIndex) => {
    setListLoading(true);
    setListError("");
    try {
      const result = await searchTickets({
        page: pageIndex - 1,
        size: PAGE_SIZE,
        sortBy: "requestId",
        sortDir: "DESC",
      });
      setTickets(result.content || []);
      setTotalItems(result.totalElements || 0);
      setTotalPages(Math.max(1, result.totalPages || 1));
      return result;
    } catch (err) {
      setTickets([]);
      setTotalItems(0);
      setTotalPages(1);
      setListError(err?.message || "Unable to load tickets.");
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTickets(page);
  }, [loadTickets, page]);

  function updateField(key, value) {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "service") {
        next.module = "";
        next.subModule = "";
      } else if (key === "module") {
        next.subModule = "";
      }
      return next;
    });
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  async function handleCreate(e) {
    e.preventDefault();
    const errors = validateTicketForm(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    setFormBanner(null);
    try {
      const remarksToKeep = form.remarks;
      const titleToMatch = form.title.trim();
      const created = await createTicket(form);
      setToast({
        message: `Ticket "${titleToMatch}" created successfully.`,
        type: "success",
      });
      setForm({
        ...EMPTY_TICKET_FORM,
        createdBy: String(getEmpObj().first_name || ""),
      });
      setFieldErrors({});
      setPage(1);
      const result = await loadTickets(1);
      const createdId = created?.requestId ?? created?.id;
      if (createdId != null && createdId !== "") {
        cacheTicketRemarks(createdId, remarksToKeep);
      } else {
        const newest = result?.content?.[0];
        const newestId = newest?.requestId ?? newest?.id;
        if (newestId != null && String(newest?.title || "") === titleToMatch) {
          cacheTicketRemarks(newestId, remarksToKeep);
        }
      }
    } catch (err) {
      setFormBanner("Couldn't save this ticket: " + (err.message || "Unknown error"));
      setToast({
        message: err?.message || "Couldn't create ticket.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  }

  const openDetail = (ticket) => {
    const id = ticket.requestId ?? ticket.id;
    if (id == null || id === "") return;
    const remarks = pickTicketRemarks(ticket);
    if (remarks) cacheTicketRemarks(id, remarks);
    navigate(`/psm/tickets/${encodeURIComponent(id)}`, { state: { ticket } });
  };

  return (
    <AppLayout hideBreadcrumb overlay={<LeaveActionToast toast={toast} />}>
      <div className="mb-3">
        <h1 className="text-xl font-bold text-foreground leading-tight">Tickets</h1>
        <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">
          Bug & enhancement request tracker
        </p>
      </div>

      <form onSubmit={handleCreate} className="mb-4">
        {formBanner ? (
          <div className="flex items-center gap-2 rounded-lg border border-[var(--color-error)]/30 bg-[var(--color-error)]/5 px-3 py-2 text-xs text-[var(--color-error)] mb-2">
            {formBanner}
          </div>
        ) : null}

        <div className="bg-white rounded-xl shadow-[0_8px_24px_-16px_rgba(30,51,131,0.3)] border border-white/60 overflow-hidden p-3 sm:p-4">
          <div className="flex items-center justify-between gap-3 mb-2.5">
            <h3 className="text-sm font-semibold text-foreground">Create Ticket</h3>
          </div>
          <TicketFormFields
            form={form}
            updateField={updateField}
            fieldErrors={fieldErrors}
            mode="create"
          />
          <div className="mt-4 flex flex-wrap items-center justify-end gap-4 border-t border-[var(--color-text-border)] pt-3">
            <PageBackLink to="/" label="Back" className="mb-0" />
            <TicketProgressField form={form} updateField={updateField} readOnly={submitting} />
            <Button
              type="submit"
              className="h-8 px-3 rounded-lg text-xs font-semibold bg-[var(--color-primary)] text-white hover:bg-[var(--color-accent)]"
              disabled={submitting}
            >
              {submitting ? "Saving…" : "Create"}
            </Button>
          </div>
        </div>
      </form>

      <h3 className="text-sm font-semibold text-foreground mb-2">Ticket History</h3>

      {listError ? (
        <div className="mb-4 rounded-xl border border-[var(--color-error)]/30 bg-[var(--color-error)]/5 px-3.5 py-2.5 text-[13px] text-[var(--color-error)]">
          {listError}
        </div>
      ) : null}

      <ContentCard>
        {listLoading ? (
          <TicketsTableSkeleton />
        ) : tickets.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title="No tickets found"
            message="Create a new ticket above to get started."
          />
        ) : (
          <>
            <div className="md:hidden divide-y divide-[var(--color-text-border)]/35">
              {tickets.map((ticket, idx) => {
                const id = ticket.requestId ?? ticket.id ?? idx;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => openDetail(ticket)}
                    className="w-full text-left px-4 py-3.5 hover:bg-[var(--color-workspace)]/20 transition-colors"
                  >
                    <p className="text-[11px] font-medium text-[var(--color-text-secondary)]">
                      {displayValue(id)}
                    </p>
                    <p className="text-sm font-semibold text-[var(--color-primary)] truncate">
                      {ticket.title || "Untitled ticket"}
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      <TicketStatusBadge status={ticket.status} />
                      <TicketPriorityBadge priority={ticket.priority} />
                      <span className="text-xs text-[var(--color-text-secondary)]">Percentage Completed: {formatTicketProgress(ticket.progressPercentage)}</span>
                    </div>
                    <p className="mt-1.5 text-xs text-[var(--color-text-secondary)]">
                      {[ticket.service, ticket.module, ticket.subModule].filter(Boolean).join(" · ") ||
                        "—"}
                    </p>
                  </button>
                );
              })}
            </div>

            <div className="hidden md:block">
              <DataTable
                minWidth="900px"
                columns={[
                  { key: "id", label: "Request ID" },
                  { key: "title", label: "Title" },
                  { key: "service", label: "Service / Module" },
                  { key: "type", label: "Type" },
                  { key: "priority", label: "Priority" },
                  { key: "status", label: "Status" },
                  { key: "progressPercentage", label: "Percentage Completed" },
                  { key: "assignee", label: "Assignee" },
                  { key: "created", label: "Created" },
                ]}
              >
                {tickets.map((ticket, idx) => {
                  const id = ticket.requestId ?? ticket.id ?? idx;
                  return (
                    <tr
                      key={id}
                      onClick={() => openDetail(ticket)}
                      className="hover:bg-[var(--color-workspace)]/20 transition-colors cursor-pointer border-b border-[var(--color-text-border)]/35 last:border-b-0"
                    >
                      <td className="px-3 py-2.5 whitespace-nowrap text-xs font-semibold text-[var(--color-primary)]">
                        {displayValue(id)}
                      </td>
                      <td className="px-3 py-2.5">
                        <p className="font-semibold text-foreground text-xs truncate max-w-[220px]">
                          {ticket.title || "Untitled ticket"}
                        </p>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-[var(--color-text-secondary)]">
                        <p className="truncate max-w-[200px]">
                          {[ticket.service, ticket.module].filter(Boolean).join(" / ") || "—"}
                        </p>
                        {ticket.subModule ? (
                          <p className="text-[10px] truncate max-w-[200px]">{ticket.subModule}</p>
                        ) : null}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-xs text-foreground">
                        {displayValue(ticket.requestType)}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <TicketPriorityBadge priority={ticket.priority} />
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <TicketStatusBadge status={ticket.status} />
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-xs text-[var(--color-text-secondary)]">
                        {formatTicketProgress(ticket.progressPercentage)}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-xs text-[var(--color-text-secondary)]">
                        {displayValue(ticket.assigneeName || ticket.assignee)}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-xs text-[var(--color-text-secondary)]">
                        {formatDate(ticket.createdOn || ticket.createdAt)}
                      </td>
                    </tr>
                  );
                })}
              </DataTable>
            </div>

            <TablePagination
              page={page}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          </>
        )}
      </ContentCard>
    </AppLayout>
  );
}
